import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import { ErrorBanner } from '@/components/ui';
import { customerBillingService, type CustomerBillingOverview } from '@/api/services/customerBilling';
import { useMe } from '@/context/MeContext';
import { formatApiError } from '@/utils/formatApiError';
import { invalidateCustomerActivityRefresh } from '@/utils/customerActivityRefresh';

function formatMoney(cents: number | undefined, currency = 'EUR'): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

function UsageMeter({
  label,
  used,
  limit,
}: {
  label: string;
  used: number;
  limit: number | null | undefined;
}) {
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;
  return (
    <div className="usage-meter">
      <div className="usage-meter__label">
        <span>{label}</span>
        <span>
          {used}
          {limit != null ? ` / ${limit}` : ' / Unlimited'}
        </span>
      </div>
      {pct != null ? (
        <div className="usage-meter__bar">
          <div className="usage-meter__fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export default function BillingPage() {
  const { me } = useMe();
  const { toast } = useToast();
  const canView = me?.permissions.canViewCustomerBilling ?? false;
  const canMutate = me?.permissions.canMutateCustomerBilling ?? false;
  const [overview, setOverview] = useState<CustomerBillingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await customerBillingService.overview();
      setOverview(data);
    } catch (e) {
      setError(formatApiError(e, 'Failed to load billing'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void load();
    else setLoading(false);
  }, [canView, load]);

  async function runAction(
    key: string,
    action: () => Promise<{
      portalUrl?: string | null;
      checkoutUrl?: string | null;
      paymentUrl?: string | null;
      message?: string;
    }>,
  ) {
    setBusy(key);
    setMessage('');
    setError('');
    try {
      const result = await action();
      const redirect = result.paymentUrl || result.checkoutUrl || result.portalUrl;
      if (redirect) {
        window.location.assign(redirect);
        return;
      }
      if (result.message) setMessage(result.message);
      await load();
      invalidateCustomerActivityRefresh();
    } catch (e) {
      setError(formatApiError(e, 'Billing action failed'));
    } finally {
      setBusy(null);
    }
  }

  if (!canView) {
    return (
      <>
        <PageHeader title="Billing" subtitle="Manage your subscription and payment details" />
        <p className="muted">You do not have permission to view billing for this account.</p>
      </>
    );
  }

  const plan = overview?.currentPlan;
  const actions = overview?.actions;

  return (
    <>
      <PageHeader title="Billing" subtitle="Manage your subscription, payment method, and invoices">
        <Link to="/usage" className="btn btn-ghost btn-sm">
          Usage details
        </Link>
        <Link to="/billing/plans" className="btn btn-ghost btn-sm">
          Compare plans
        </Link>
      </PageHeader>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}
      {message ? <p className="alert alert-info">{message}</p> : null}

      {loading && !overview ? (
        <p className="muted">Loading billing…</p>
      ) : overview ? (
        <div className="billing-overview-grid">
          <section className="card card-elevated">
            <h2>Current plan</h2>
            {plan ? (
              <>
                <p className="billing-plan-name">{plan.marketingTitle || plan.name}</p>
                <p className="muted">
                  {formatMoney(plan.priceCents, plan.currency)} / {plan.billingInterval || 'month'}
                </p>
              </>
            ) : (
              <p className="muted">No active subscription plan.</p>
            )}
            <dl className="detail-grid billing-status-grid">
              <dt>Status</dt>
              <dd>{overview.lifecycleLabel || overview.status}</dd>
              <dt>Payment</dt>
              <dd>{overview.paymentStatus || '—'}</dd>
              <dt>Auto-renew</dt>
              <dd>
                {overview.cancelAtPeriodEnd
                  ? 'Cancels at period end'
                  : overview.autoRenew
                    ? 'Enabled'
                    : 'Disabled'}
              </dd>
              <dt>Renews / expires</dt>
              <dd>
                {overview.currentPeriodEnd
                  ? new Date(overview.currentPeriodEnd).toLocaleString()
                  : overview.expiresAt
                    ? new Date(overview.expiresAt).toLocaleString()
                    : '—'}
              </dd>
              {overview.graceUntil ? (
                <>
                  <dt>Grace until</dt>
                  <dd>{new Date(overview.graceUntil).toLocaleString()}</dd>
                </>
              ) : null}
            </dl>
          </section>

          <section className="card card-elevated">
            <h2>Usage</h2>
            <UsageMeter label="Active searches" used={overview.usage.searches.used} limit={overview.usage.searches.limit} />
            <UsageMeter label="Recipients" used={overview.usage.recipients.used} limit={overview.usage.recipients.limit} />
            <UsageMeter
              label="Notifications today"
              used={overview.usage.notificationsToday.used}
              limit={overview.usage.notificationsToday.limit}
            />
          </section>

          {canMutate && actions ? (
            <section className="card card-elevated billing-actions-card">
              <h2>Manage subscription</h2>
              <div className="billing-actions">
                {actions.canChangePlan ? (
                  <Link to="/billing/plans" className="btn btn-sm">
                    Change plan
                  </Link>
                ) : null}
                {actions.canManagePaymentMethod ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={busy != null}
                    onClick={() => void runAction('portal', () => customerBillingService.portal())}
                  >
                    {busy === 'portal' ? 'Opening…' : 'Manage payment method'}
                  </button>
                ) : null}
                {actions.canRetryPayment ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-warning"
                    disabled={busy != null}
                    onClick={() => void runAction('retry', () => customerBillingService.retryPayment())}
                  >
                    {busy === 'retry' ? 'Opening…' : 'Retry payment'}
                  </button>
                ) : null}
                {actions.canResume ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy != null}
                    onClick={() => void runAction('resume', () => customerBillingService.resume())}
                  >
                    {busy === 'resume' ? 'Working…' : 'Resume subscription'}
                  </button>
                ) : null}
                {actions.canCancel ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={busy != null}
                    onClick={() => setConfirmCancel(true)}
                  >
                    {busy === 'cancel' ? 'Working…' : 'Cancel subscription'}
                  </button>
                ) : null}
              </div>
              {!overview.onlinePaymentsAvailable ? (
                <p className="muted billing-hint">
                  Online payments are not currently available. Contact support for plan changes.
                </p>
              ) : null}
            </section>
          ) : null}

          <section className="card card-elevated">
            <h2>History</h2>
            <p className="muted">View invoices, payments, and billing events.</p>
            <div className="billing-actions">
              <Link to="/billing/invoices" className="btn btn-sm btn-ghost">
                Invoices
              </Link>
              <Link to="/billing/history" className="btn btn-sm btn-ghost">
                Payment history
              </Link>
            </div>
          </section>
        </div>
      ) : null}
      <ConfirmActionModal
        open={confirmCancel}
        title="Cancel subscription?"
        description="Your subscription will remain active until the end of the current billing period."
        confirmLabel="Cancel subscription"
        variant="danger"
        loading={busy === 'cancel'}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={async () => {
          await runAction('cancel', () => customerBillingService.cancel());
          toast('Subscription will cancel at period end', 'success');
          setConfirmCancel(false);
        }}
      />
    </>
  );
}
